import { ChangeDetectorRef, Component, computed, signal, ViewChild } from '@angular/core';
import { DataService } from '../../services/data';
import { CommonModule } from '@angular/common';
import { TagModule } from 'primeng/tag';
import { Table, TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { MultiSelectModule } from 'primeng/multiselect';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ProgressBarModule } from 'primeng/progressbar';
import { DatePickerModule } from 'primeng/datepicker';

import * as XLSX from 'xlsx';

interface Column {
  field: string;
  header: string;
}

interface DistributionItem {
  label: string;
  value: number;
  percent: number;
}

@Component({
  selector: 'app-list',
  imports: [
    TableModule,
    TagModule,
    InputTextModule,
    InputIconModule,
    IconFieldModule,
    CommonModule,
    MultiSelectModule,
    FormsModule,
    ButtonModule,
    ProgressSpinnerModule,
    ProgressBarModule,
    DatePickerModule,
  ],
  templateUrl: './list.html',
  styleUrl: './list.scss',
})
export class ListComponent {
  listData = signal<any[]>([]);
  filteredData = signal<any[]>([]);
  exportProgress = signal(0);
  isDashboardExpanded = signal(false);

  readonly totalRecords = computed(() => this.filteredData().length);
  readonly totalMessages = computed(() =>
    this.filteredData().reduce((sum, item) => sum + this.toNumber(item.messages), 0)
  );
  readonly totalSize = computed(() =>
    this.filteredData().reduce((sum, item) => sum + this.toNumber(item.size), 0)
  );
  readonly avgMessageSize = computed(() => {
    const messages = this.totalMessages();
    if (!messages) {
      return 0;
    }

    return this.totalSize() / messages;
  });

  readonly byDate = computed(() => this.buildDistribution(this.filteredData(), 'date'));
  readonly byType = computed(() => this.buildDistribution(this.filteredData(), 'messagetype'));
  readonly bySender = computed(() => this.buildDistribution(this.filteredData(), 'sender'));
  readonly byReceiver = computed(() => this.buildDistribution(this.filteredData(), 'receiver'));

  loading = true;
  isLoading = true;

  @ViewChild('dtModern') table!: Table;

  cols!: Column[];
  selectedColumns!: Column[];

  filters = {
    platform: '',
    messageType: '',
    dateRange: '' as any,
    sender: '',
    receiver: '',
  };
  rowCount: number = 10;

  constructor(
    private dataService: DataService,
    private cd: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.listData.set([]);
    this.cols = [
      { field: 'messages', header: 'Messages' },
      { field: 'maxsize', header: 'Maxsize' },
      { field: 'size', header: 'Size' },
    ];
    this.selectedColumns = this.cols;

    this.loadData();
    this.updateRows(window.innerWidth);
  }

  ngAfterViewInit() {
    this.table?.clear();
    this.cd.detectChanges();
  }

  clear(table: Table) {
    table.clear();
  }

  toggleDashboard() {
    this.isDashboardExpanded.update((current) => !current);
  }

  loadData() {
    this.isLoading = true;
    let [startDate, endDate] = this.filters?.dateRange;
    startDate = this.formatDate(startDate);
    endDate = this.formatDate(endDate);
    this.dataService.getAllData({ startDate, endDate }).subscribe((data: any) => {
      let resultData = data.items;
      let i = 0;

      resultData = resultData.map((d: any) => ({
        ...d,
        platform: d.flowid,
        dateValue: new Date(d.date),
        messagetype : d.msgtype,
        messages:d.totcount,
        size : d.totsize,
        id: i++,
      }));

      this.loading = false;

      this.listData.set(resultData);
      // if (this.filters?.dateRange)
      // this.clearFilters();
      this.filteredData.set(this.listData());
      this.isLoading = false;
    });
  }

  applyFilters() {
    let data = this.listData();

    if (this.filters.platform) {
      data = data.filter((x) =>
        x.system.toLowerCase().includes(this.filters.platform.toLowerCase())
      );
    }

    if (this.filters.messageType) {
      data = data.filter((x) =>
        x.messagetype.toUpperCase().includes(this.filters.messageType?.toUpperCase())
      );
    }

    if (this.filters.sender) {
      data = data.filter((x) =>
        x.sender.toLowerCase().includes(this.filters.sender.toLowerCase())
      );
    }

    if (this.filters.receiver) {
      data = data.filter((x) =>
        x.receiver.toLowerCase().includes(this.filters.receiver.toLowerCase())
      );
    }

    // if (this.filters.dateRange && (this.filters.dateRange[0] && this.filters.dateRange[1])) {
      //   let [startDate, endDate] = this.filters.dateRange;
      //   startDate = this.formatDate(startDate);
      //   endDate = this.formatDate(endDate);

      //   data = data.filter((x) => {
      //     const itemDate = this.formatDate(x.dateValue);
      //     if (startDate && endDate) {
      //       return itemDate >= startDate && itemDate <= endDate;
      //     } else if (startDate) {
      //       return itemDate === startDate;
      //     } else if (endDate) {
      //       return itemDate === endDate;
      //     }
      //     return true;
      //   });
    //   this.loadData();
    // }

    this.filteredData.set(data);
  }

  clearFilters() {
    console.log('Clear filter called');

    this.filters = {
      platform: '',
      messageType: '',
      dateRange: '',
      sender: '',
      receiver: '',
    };
    this.loadData()

  }

  saveAsExcelFile(buffer: any, fileName: string) {
    const blob = new Blob([buffer], {
      type: 'application/octet-stream',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  }

  exportExcelWithProgress() {
    if (this.exportProgress() > 0) {
      return;
    }

    const data = this.table.filteredValue || this.table.value;

    if (!data || data.length === 0) return;

    const chunkSize = 100;
    const totalChunks = Math.ceil(data.length / chunkSize);
    let currentChunk = 0;
    const exportData: any[] = [];

    const processChunk = () => {
      const start = currentChunk * chunkSize;
      const end = start + chunkSize;
      const chunk = data.slice(start, end);
      const cols = [
        'platform',
        'system',
        'date',
        'sender',
        'receiver',
        'messagetype',
        'messages',
        'size',
        'maxsize',
      ];

      chunk.forEach((row: any) => {
        const obj: any = {};
        cols.forEach((col) => {
          obj[col] = row[col];
        });
        exportData.push(obj);
      });

      currentChunk++;
      this.exportProgress.set(Math.round((currentChunk / totalChunks) * 100));

      if (currentChunk < totalChunks) {
        setTimeout(processChunk, 400);
      } else {
        this.generateExcel(exportData);
        this.exportProgress.set(0);
      }
    };

    processChunk();
  }

  generateExcel(exportData: any[]) {
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = {
      Sheets: { Data: worksheet },
      SheetNames: ['Data'],
    };

    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(buffer, 'Report');
  }

  formatDate(date: any): string {
    if (!date) return '';

    const d = new Date(date);
    const year = d.getFullYear();
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);

    return `${year}-${month}-${day}`;
  }

  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  }

  formatBytes(bytes: number): string {
    if (bytes <= 0) {
      return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const size = bytes / 1024 ** exponent;

    return `${size.toFixed(size >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
  }

  private toNumber(value: unknown): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private buildDistribution(data: any[], field: string): DistributionItem[] {
    if (!data.length) {
      return [];
    }

    const summary = new Map<string, number>();

    for (const item of data) {
      const rawValue = item?.[field];
      const label = typeof rawValue === 'string' && rawValue.trim() ? rawValue : 'Unknown';
      const current = summary.get(label) ?? 0;
      summary.set(label, current + this.toNumber(item.messages));
    }

    const sorted = [...summary.entries()].sort((a, b) => b[1] - a[1]);
    const maxValue = sorted[0]?.[1] ?? 0;

    return sorted.slice(0, 6).map(([label, value]) => ({
      label,
      value,
      percent: maxValue ? Math.round((value / maxValue) * 100) : 0,
    }));
  }

  updateRows(width: number) {
    if (width >= 2560) {
      this.rowCount = 20;
    } else {
      this.rowCount = 10;
    }
  }
  applyFiltersOnDate() {
    if (this.filters.dateRange && (this.filters.dateRange[0] && this.filters.dateRange[1])) {
      this.loadData();
    }
  }
}